import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../components/ui/Button';
import { CandidatesSheet } from '../../components/CandidatesSheet';
import { OperatorScanPanel } from '../../components/OperatorScanPanel';
import { QueueList } from '../../components/QueueList';
import { taskEntered } from '../../../../features/operator/operatorDeviceSlice';
import { OPERATOR_FLOW, OPERATOR_FLOW_PATHS } from '../../../../features/operator/operatorConstants';
import { fetchKits, findKit } from '../../../../features/operatorKits/operatorKitsThunks';
import { kitChoiceDismissed, kitStationEntered } from '../../../../features/operatorKits/operatorKitsSlice';
import { kitFree, kitRowCaption } from '../../../../features/operatorKits/operatorKitsUtils';

const kitPath = (kitId) => `${OPERATOR_FLOW_PATHS[OPERATOR_FLOW.KITS]}/${kitId}`;

/**
 * Kits: the kits put together in this warehouse, the ones that can be made
 * now first. Scanning a kit — its barcode, SKU or a pack's code — opens it; a
 * code of several asks which. Free work: there are no tasks.
 */
export const KitStation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list, station } = useSelector((state) => state.operatorKits);

  useEffect(() => {
    dispatch(kitStationEntered());
    dispatch(taskEntered({ flow: OPERATOR_FLOW.KITS }));
    dispatch(fetchKits({ page: 1 }));
  }, [dispatch]);

  // Opens what was found only while the station is on screen: meanwhile the operator may have opened another.
  const openScanned = async (code, source) => {
    const action = await dispatch(findKit({ code, source }));
    if (findKit.fulfilled.match(action) && !action.meta.offScreenTask && action.payload.kind === 'kit') {
      navigate(kitPath(action.payload.kit.id));
    }
  };

  const choose = (kitId) => {
    dispatch(kitChoiceDismissed());
    navigate(kitPath(kitId));
  };

  return (
    <div className="space-y-5" data-testid="kit-station">
      <OperatorScanPanel
        prompt="Escanea el kit"
        hint="Su código, su SKU o el de su caja. O tócalo en la lista."
        accepting={!station.finding}
        lastCode={station.lastCode}
        holdReads={false}
        busyLabel="Buscando…"
        onCode={openScanned}
      />

      <section className="space-y-3" aria-labelledby="kit-list-title">
        <div className="flex items-baseline justify-between gap-3">
          <h2 id="kit-list-title" className="text-base font-bold text-ninja-dark dark:text-white">
            Kits de esta bodega
          </h2>
          {!list.loading && list.count > 0 && (
            <span className="text-sm text-text-secondary">
              {list.count} {list.count === 1 ? 'kit' : 'kits'}
            </span>
          )}
        </div>
        <QueueList
          queue={list}
          testId="kit-list"
          emptyText="No hay kits armables en esta bodega."
          onRetry={() => dispatch(fetchKits({ page: 1 }))}
          onMore={() => dispatch(fetchKits({ page: list.page + 1 }))}
        >
          {list.items.map((row) => (
            <li key={row.id}>
              <Button
                variant="choice"
                data-testid="kit-row"
                data-kit-id={row.id}
                data-can-assemble={Number(row.can_assemble) || 0}
                data-in-stock={kitFree(row)}
                onClick={() => navigate(kitPath(row.id))}
              >
                <span className="block font-bold text-ninja-dark dark:text-white">{row.sku}</span>
                <span className="block truncate text-sm text-text-secondary">
                  {[row.name, row.company?.name].filter(Boolean).join(' · ')}
                </span>
                <span className="mt-1 block text-sm font-semibold text-ninja-dark dark:text-white">{kitRowCaption(row)}</span>
              </Button>
            </li>
          ))}
        </QueueList>
      </section>

      <CandidatesSheet
        isOpen={Boolean(station.choice)}
        title="¿Qué kit es?"
        message={station.choice?.message}
        options={station.choice?.options}
        onChoose={choose}
        onClose={() => dispatch(kitChoiceDismissed())}
      />
    </div>
  );
};
